import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { inr } from "../lib/format";
import { netPnlByOutcome } from "../lib/exposure";
import Odds from "./Odds";
import BetSlip from "./BetSlip";
import SessionMarket from "./SessionMarket";
import { TEAMS } from "../lib/site";

// Renders the betting markets for a single match (back/lay + bet slip).
// Self-contained selection state so it can be dropped inline anywhere
// (match detail page, expandable list rows, etc.).
export default function MatchMarkets({ match }) {
  const { bets } = useApp();
  const [selection, setSelection] = useState(null);

  const home = match.homeTeam || TEAMS[match.home] || { code: match.home, name: match.home };
  const away = match.awayTeam || TEAMS[match.away] || { code: match.away, name: match.away };
  const isCompleted = match.status === "completed";
  const bettingDisabled = isCompleted || !!match.bettingSuspended;

  const pick = (market, option, side = "back") => {
    if (bettingDisabled) return;
    const rate = side === "lay" ? option.lay : (option.back ?? option.odds);
    if (!rate) return;
    setSelection({
      matchId: match.id,
      matchLabel: `${home.code || match.home} vs ${away.code || match.away}`,
      marketKey: market.key,
      marketName: market.name,
      selectionId: option.id,
      selectionLabel: option.label,
      odds: rate,
      side,
    });
  };

  return (
    <div className="space-y-3">
      {match.bettingSuspended && !isCompleted && (
        <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 p-3 text-center" data-testid="match-suspended-banner">
          <div className="font-display font-semibold text-amber-300 text-sm">Betting suspended</div>
          <div className="text-[11px] text-amber-200/80 mt-0.5">Odds are temporarily on hold. Please check back shortly.</div>
        </div>
      )}

      {(match.markets || []).map((market) => {
        if (market.key === "winner") {
          const outcomes = market.options.map((o) => o.label);
          const openWinner = (bets || []).filter(
            (b) => b.matchId === match.id && b.marketName === market.name && b.status === "matched"
          );
          const pnl = openWinner.length ? netPnlByOutcome(openWinner, outcomes) : {};
          const held = !!market.held;
          const marketDead = market.suspended || market.options.some((o) => {
            const b = o.back, l = o.lay;
            if (!b && l && l <= 1.10) return true;
            if (!l && b && b <= 1.10 && "lay" in o) return true;
            if (b && l && (l < b - 0.001 || l > b * 1.25)) return true;
            return false;
          });
          return (
            <div key={market.key} className="rounded-xl border border-border bg-card p-3 sm:p-4" data-testid="market-winner">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold text-sm">{market.name}</h3>
                {market.suspended && market.suspendReason && !market.held && (
                  <span data-testid="winner-suspend-reason" className="text-[11px] font-semibold text-amber-300 truncate max-w-[60%] sm:hidden">{market.suspendReason}</span>
                )}
                <div className="hidden sm:grid grid-cols-2 gap-2 w-[188px] text-center text-[11px] font-semibold uppercase tracking-wide">
                  <span className="text-sky-400">Lagai · Back</span>
                  <span className="text-rose-400">Khai · Lay</span>
                </div>
              </div>
              {market.suspended && market.suspendReason && !market.held && (
                <div data-testid="winner-suspend-banner" className="hidden sm:block mb-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-center text-[12px] font-semibold text-amber-300">{market.suspendReason}</div>
              )}
              <div className="space-y-2">
                {market.options.map((o) => {
                  const backActive = selection && selection.marketKey === market.key && selection.selectionId === o.id && selection.side === "back";
                  const layActive = selection && selection.marketKey === market.key && selection.selectionId === o.id && selection.side === "lay";
                  const isWinner = isCompleted && o.won;
                  const v = openWinner.length ? Math.round(pnl[o.label] || 0) : null;
                  const backLocked = o.lay != null && o.lay > 0 && o.lay < 0.035;
                  return (
                    <div key={o.id} className="flex items-center gap-2 rounded-xl border border-border bg-secondary/50 px-2.5 sm:px-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-medium truncate">
                          {o.label}{isWinner && <span className="ml-2 text-xs text-primary">✓ Won</span>}
                        </span>
                        {v !== null && (
                          <span data-testid={`pnl-${o.id}`} className={`text-[11px] font-semibold ${v > 0 ? "text-emerald-400" : v < 0 ? "text-red-400" : "text-muted-foreground"}`}>
                            {v > 0 ? "+" : ""}{inr(v)}
                          </span>
                        )}
                        {(o.suspended || backLocked || marketDead) && !held && (
                          <span data-testid={`suspended-${o.id}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-amber-400">Suspended</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 w-[132px] sm:w-[188px] shrink-0">
                        <button
                          data-testid={`back-${o.id}`}
                          onClick={() => pick(market, o, "back")}
                          disabled={bettingDisabled || !o.back || backLocked || marketDead}
                          className={`flex flex-col items-center rounded-lg px-2 py-1.5 transition-[color,background-color,border-color,opacity,transform] disabled:opacity-40 disabled:cursor-not-allowed ${held ? "bg-zinc-600" : backActive ? "ring-2 ring-white bg-[#5AAEEA]" : "bg-[#72BBEF] hover:bg-[#5AAEEA]"}`}
                        >
                          <span className={`font-display font-bold text-sm leading-none ${held ? "text-zinc-200" : "text-slate-900"}`}>{o.back && (!marketDead || held) ? <Odds value={o.back} showArrow /> : "—"}</span>
                          <span className={`text-[9px] mt-0.5 ${held ? "text-zinc-400" : "text-slate-800/80"}`}>Lagai</span>
                        </button>
                        {o.lay && (!marketDead || held) ? (
                          <button
                            data-testid={`lay-${o.id}`}
                            onClick={() => pick(market, o, "lay")}
                            disabled={bettingDisabled || marketDead}
                            className={`flex flex-col items-center rounded-lg px-2 py-1.5 transition-[color,background-color,border-color,opacity,transform] disabled:opacity-40 disabled:cursor-not-allowed ${held ? "bg-zinc-600" : layActive ? "ring-2 ring-white bg-[#F77FAE]" : "bg-[#F994BA] hover:bg-[#F77FAE]"}`}
                          >
                            <span className={`font-display font-bold text-sm leading-none ${held ? "text-zinc-200" : "text-slate-900"}`}><Odds value={o.lay} showArrow /></span>
                            <span className={`text-[9px] mt-0.5 ${held ? "text-zinc-400" : "text-slate-800/80"}`}>Khai</span>
                          </button>
                        ) : (
                          <div className="grid place-items-center rounded-lg bg-secondary/40 text-[10px] text-muted-foreground">—</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        return (
          <div key={market.key} className="rounded-xl border border-border bg-card p-3 sm:p-4">
            <h3 className="font-display font-semibold text-sm mb-3">{market.name}</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {market.options.map((o) => {
                const active = selection && selection.marketKey === market.key && selection.selectionId === o.id;
                const isWinner = isCompleted && o.won;
                return (
                  <button
                    key={o.id}
                    onClick={() => pick(market, o)}
                    disabled={bettingDisabled}
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-[color,background-color,border-color,opacity,transform] ${active ? "border-primary bg-primary/15" : isWinner ? "border-primary/40 bg-primary/5" : "border-border bg-secondary hover:border-primary/40 disabled:opacity-60 disabled:cursor-not-allowed"}`}
                  >
                    <span className="text-sm font-medium truncate">
                      {o.label}{isWinner && <span className="ml-2 text-xs text-primary">✓ Won</span>}
                    </span>
                    <span className={`font-display font-bold ${active ? "text-primary" : "text-foreground"}`}><Odds value={o.odds} showArrow /></span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      <SessionMarket match={match} type="session" />
      <SessionMarket match={match} type="lambi" />

      <div className="text-right">
        <Link to={`/match/${match.id}`} className="text-xs font-semibold text-primary hover:underline">Open full match →</Link>
      </div>

      <BetSlip
        selection={selection}
        outcomes={(() => {
          if (!selection) return [];
          const mk = (match.markets || []).find((m) => m.key === selection.marketKey);
          return (mk?.options || []).map((o) => o.label);
        })()}
        liveOdds={(() => {
          if (!selection) return null;
          const mk = (match.markets || []).find((m) => m.key === selection.marketKey);
          const opt = mk?.options.find((o) => o.id === selection.selectionId);
          if (!opt) return null;
          return selection.side === "lay" ? opt.lay : (opt.back ?? opt.odds);
        })()}
        onClear={() => setSelection(null)}
      />
    </div>
  );
}
